/**
 * Códigos de erro estáveis. A mensagem exibida ao jogador vem da camada de
 * i18n do frontend (chave `errors.<CODE>`); `message` é apenas um texto de
 * apoio para logs e para clientes sem tradução.
 */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'RATE_LIMITED',
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'ROOM_ALREADY_STARTED',
  'ROOM_CLOSED',
  'INVALID_CODE',
  'NAME_TAKEN',
  'NAME_INVALID',
  'NOT_IN_ROOM',
  'NOT_HOST',
  'NOT_ENOUGH_PLAYERS',
  'INVALID_PHASE',
  'INVALID_CONFIG',
  'CARD_NOT_FOUND',
  'CARD_NOT_PLAYABLE',
  'STALE_ACTION',
  'NO_STARS_LEFT',
  'STAR_VOTE_ACTIVE',
  'ALREADY_VOTED',
  'PLAYER_NOT_FOUND',
  'SESSION_INVALID',
  'CANNOT_END_GAME_YET',
  'INTERNAL_ERROR',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export type AppErrorPayload = {
  code: ErrorCode;
  /** Texto de apoio, não traduzido. O cliente prefere a chave de i18n. */
  message: string;
  /** Dados mínimos necessários para a interface. */
  details?: Record<string, string | number | boolean>;
};

const DEFAULT_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'Dados inválidos.',
  RATE_LIMITED: 'Muitas tentativas. Aguarde um instante.',
  ROOM_NOT_FOUND: 'Sala não encontrada.',
  ROOM_FULL: 'A sala está cheia.',
  ROOM_ALREADY_STARTED: 'A partida já começou.',
  ROOM_CLOSED: 'A sala foi encerrada.',
  INVALID_CODE: 'Código inválido.',
  NAME_TAKEN: 'Já existe alguém com esse apelido na sala.',
  NAME_INVALID: 'Apelido inválido.',
  NOT_IN_ROOM: 'Você não está nesta sala.',
  NOT_HOST: 'Apenas o anfitrião pode fazer isso.',
  NOT_ENOUGH_PLAYERS: 'São necessários pelo menos 2 jogadores conectados.',
  INVALID_PHASE: 'Ação indisponível nesta fase da partida.',
  INVALID_CONFIG: 'Configuração inválida.',
  CARD_NOT_FOUND: 'Carta não encontrada.',
  CARD_NOT_PLAYABLE: 'Só é possível jogar a menor carta da mão.',
  STALE_ACTION: 'Esta jogada não é mais válida.',
  NO_STARS_LEFT: 'Não há estrelas disponíveis.',
  STAR_VOTE_ACTIVE: 'Já existe uma votação em andamento.',
  ALREADY_VOTED: 'Você já votou.',
  PLAYER_NOT_FOUND: 'Jogador não encontrado.',
  SESSION_INVALID: 'Sessão inválida ou expirada.',
  CANNOT_END_GAME_YET: 'Ainda é possível aguardar a reconexão.',
  INTERNAL_ERROR: 'Ocorreu um erro inesperado.',
};

export function appError(
  code: ErrorCode,
  details?: Record<string, string | number | boolean>,
): AppErrorPayload {
  return details
    ? { code, message: DEFAULT_MESSAGES[code], details }
    : { code, message: DEFAULT_MESSAGES[code] };
}

export function defaultErrorMessage(code: ErrorCode): string {
  return DEFAULT_MESSAGES[code];
}
